import type {
  ForecastState,
  Freshness,
  PhysicalBridgeState,
  PublicVesselSummary,
  VesselDirection,
  VesselKind,
} from "../shared/contracts";

const ET_ZONE = "America/New_York";

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_ZONE,
  hour: "numeric",
  minute: "2-digit",
});

const clockFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_ZONE,
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
});

const fullTimeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_ZONE,
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
});

export function formatEtTime(value: string | Date): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return `${timeFormatter.format(date)} ET`;
}

export function formatEtClock(value: string | Date): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return `${clockFormatter.format(date)} ET`;
}

export function formatEtTimestamp(value: string | null): string {
  if (!value) return "Not yet confirmed";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return `${fullTimeFormatter.format(date)} ET`;
}

export function formatDistanceToBrickell(value: number | null): string | null {
  if (value === null || !Number.isFinite(value) || value < 0) return null;
  const miles = value / 1_609.344;
  return miles < 0.1 ? "<0.1 mi to Brickell" : `${miles.toFixed(1)} mi to Brickell`;
}

export const forecastCopy: Record<
  ForecastState,
  { label: string; detail: string }
> = {
  clear: {
    label: "NO OPENING EXPECTED",
    detail: "No opening is expected at Brickell in the next 30 minutes.",
  },
  likely: {
    label: "OPENING LIKELY",
    detail: "The bridge may stop traffic within 30 minutes. Allow extra crossing time.",
  },
  open: {
    label: "SPAN IS UP",
    detail: "The bridge is raised for river traffic now.",
  },
  unavailable: {
    label: "OUTLOOK UNAVAILABLE",
    detail: "We’ll restore the live outlook as soon as it reconnects.",
  },
};

export const bridgeTrafficCopy: Record<
  PhysicalBridgeState,
  { label: string; consequence: string }
> = {
  down: { label: "BRIDGE CLOSED", consequence: "Traffic is flowing." },
  up: { label: "BRIDGE OPEN", consequence: "Traffic is blocked." },
  unknown: { label: "STATUS UNKNOWN", consequence: "Traffic flow cannot be confirmed." },
};

export const bridgeCopy: Record<
  PhysicalBridgeState,
  { label: string; consequence: string }
> = {
  down: { label: "TRAFFIC MOVING", consequence: "Bridge down" },
  up: { label: "TRAFFIC STOPPED", consequence: "Bridge up" },
  unknown: { label: "TRAFFIC STATUS UNKNOWN", consequence: "Position not confirmed" },
};

export const bridgePositionCopy: Record<PhysicalBridgeState, string> = {
  down: "BRIDGE DOWN",
  up: "BRIDGE UP",
  unknown: "POSITION NOT CONFIRMED",
};

export function freshnessLabel(
  freshness: Freshness,
  dataMode: "demo" | "live" = "live",
): string {
  if (dataMode === "demo") return "Demo";
  switch (freshness) {
    case "live":
      return "Live";
    case "delayed":
      return "Delayed";
    case "stale":
      return "Offline";
    default:
      return "Freshness unknown";
  }
}

export function directionLabel(direction: VesselDirection): string {
  switch (direction) {
    case "inbound":
      return "Inbound";
    case "outbound":
      return "Outbound";
    case "holding":
      return "Holding";
    default:
      return "Direction unknown";
  }
}

export function vesselDisplayName(vessel: Pick<PublicVesselSummary, "name" | "type">): string {
  return vessel.name?.trim() || `${vesselKindLabel(vessel.type)} vessel`;
}

export function vesselKindLabel(kind: VesselKind): string {
  return kind === "generic" ? "Unknown type" : titleCase(kind);
}

export function titleCase(value: string): string {
  return value
    .split(/[-_\s]+/u)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function numeric(value: number | null, unit: string, digits = 0): string | null {
  return value === null || !Number.isFinite(value) ? null : `${value.toFixed(digits)} ${unit}`;
}
